import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import MattersCombobox from "../Postings/MattersComboBox.jsx";
import { Grid, GridColumn, GridNoRecords } from "@progress/kendo-react-grid";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import SearchTextBox from "components/SearchComponent.jsx";
import { Button } from "@progress/kendo-react-buttons";
import { arrowRotateCwIcon } from "@progress/kendo-svg-icons";
import { filterBy } from "@progress/kendo-data-query";
import { toast } from "react-toastify";
import {
  AmountFormat,
  DateFormatCell,
  formatDateForAPI,
} from "components/common/formatter.js";
import CalenderCustomToggleButton from "components/common/CalenderCustomToogleButton.jsx";
import { GRID_PAGE_SIZES } from "components/common/GridComponents";
import { GridLoader } from "components/GridLoader.jsx";
import ViewPostingDetails from "pages/Postings/PostingDetails";
import { changeLedgerLink } from "actions/ledgerLinks.action";
import { fetchSuppliersData } from "services/supplierService";
import { useAllNominals } from "services/nominalsService";
import { useCompany } from "services/companyService";
import { TransactionSearchRowMenu } from "./TransactionSearchRowMenu";
import * as api from "api/accountsAPI.js";

const NullableAmountCell = (props) => {
  const value = props.dataItem[props.field];
  return (
    <td className="text-end">
      {value && value !== 0 ? AmountFormat(value) : ""}
    </td>
  );
};

const NetAmountCell = (props) => {
  const gross = props.dataItem.grossAmount || 0;
  const vat = props.dataItem.vatAmount || 0;
  const net = gross - vat;
  return <td className="text-end">{net !== 0 ? AmountFormat(net) : ""}</td>;
};

const PayeeDescCell = (props) => {
  const payee = props.dataItem.payee || "";
  const details = props.dataItem.details || "";
  const combined = [payee, details].filter(Boolean).join(" — ");
  return <td>{combined}</td>;
};

const TransactionSearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const searchRef = useRef();
  const suppressDataStateFetchRef = useRef(false);
  const skipInitialFilterEffectRef = useRef(true);
  const { data: company } = useCompany();
  const { data: allNominals } = useAllNominals();

  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [showDetails, setShowDetails] = useState(false);
  const [detailsTransNo, setDetailsTransNo] = useState("");
  const [selectedCell, setSelectedCell] = useState(null);

  const [matterValue, setMatterValue] = useState(null);
  const [supplierList, setSupplierList] = useState([]);
  const [supplierFilterData, setSupplierFilterData] = useState([]);
  const [supplierValue, setSupplierValue] = useState(null);
  const [nominalFilterData, setNominalFilterData] = useState([]);
  const [nominalValue, setNominalValue] = useState(null);
  const [useFrom, setUseFrom] = useState(false);
  const [useTo, setUseTo] = useState(false);
  const [fromDateSelector, setFromDateSelector] = useState(new Date());
  const [toDateSelector, setToDateSelector] = useState(new Date());

  const [list, setList] = useState({ data: [], total: 0 });
  const [dataState, setDataState] = useState({
    take: 100,
    skip: 0,
    sort: [{ field: "date", dir: "desc" }],
  });

  const nominalDisplayList = React.useMemo(() => {
    if (!allNominals?.length) return [];
    return allNominals.map((n) => ({
      ...n,
      display: `${n.nominalRef} — ${n.nominalName ?? ""}`,
    }));
  }, [allNominals]);

  useEffect(() => {
    setNominalFilterData(nominalDisplayList);
  }, [nominalDisplayList]);

  useEffect(() => {
    if (company?.yearStartDate) {
      setFromDateSelector(new Date(company.yearStartDate));
    }
  }, [company?.yearStartDate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchSuppliersData({ active: true });
        if (cancelled) return;
        const withRep = rows.map((item) => ({
          ...item,
          representation: `${item.supplierRef}  -  ${item.supplierName}`,
        }));
        setSupplierList(withRep);
        setSupplierFilterData(withRep);
      } catch {
        if (!cancelled) {
          setSupplierList([]);
          setSupplierFilterData([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const buildFilterParameters = () => {
    const params = {};
    if (matterValue?.matterId) params.matterId = matterValue.matterId;
    if (supplierValue?.supplierRef)
      params.supplierRef = supplierValue.supplierRef;
    if (nominalValue?.nominalRef) params.nominalRef = nominalValue.nominalRef;
    if (useFrom && fromDateSelector) {
      params.fromDate = formatDateForAPI(new Date(fromDateSelector));
    }
    if (useTo && toDateSelector) {
      params.toDate = formatDateForAPI(new Date(toDateSelector));
    }
    return params;
  };

  const sortFieldToApi = (field) => {
    const map = {
      date: "Date",
      postingType: "PostingType",
      matterRef: "MatterRef",
      reference: "Reference",
      details: "Details",
      supplierRef: "SupplierRef",
      nominalRef: "NominalRef",
      vatAmount: "VatAmount",
      grossAmount: "GrossAmount",
    };
    return map[field] || field;
  };

  const buildOrderBy = (sortParam) => {
    if (!sortParam?.field) return "Date desc";
    const apiField = sortFieldToApi(sortParam.field);
    return sortParam.dir === "desc"
      ? `${apiField} desc`
      : `${apiField} asc`;
  };

  const fetchData = async (resetPaging = false) => {
    const skipBeforeFetch = dataState.skip;
    setLoading(true);
    try {
      const currentDataState = resetPaging
        ? { ...dataState, skip: 0 }
        : dataState;
      const page =
        Math.floor(currentDataState.skip / currentDataState.take) + 1;
      const limit = currentDataState.take;
      const sortParam = currentDataState.sort?.[0];
      const orderBy = buildOrderBy(sortParam);

      const filterParams = buildFilterParameters();
      const response = await api.getTransactionSearchList(
        page,
        limit,
        orderBy,
        filterParams,
        searchValue || undefined,
      );

      const responseData = response.data || {};
      const rawItems = responseData.data || responseData.items || [];

      const uniqueItems = rawItems.filter(
        (item, index, self) =>
          index ===
          self.findIndex((t) => t.transactionNo === item.transactionNo),
      );

      const total =
        responseData.total ||
        responseData.totalCount ||
        responseData.count ||
        uniqueItems.length;

      setList((prev) => ({ data: uniqueItems, total }));

      if (resetPaging && skipBeforeFetch !== 0) {
        suppressDataStateFetchRef.current = true;
        setDataState((prev) => ({ ...prev, skip: 0 }));
      }
    } catch (error) {
      console.error("Transaction search error:", error);
      setList({ data: [], total: 0 });
    } finally {
      setLoading(false);
    }
  };

  const onMatterChange = (event) => {
    const v = event?.target?.value;
    setMatterValue(v && v.matterId ? v : null);
  };

  const onSupplierChange = (event) => {
    setSupplierValue(event.value);
  };

  const onNominalChange = (event) => {
    setNominalValue(event.value);
  };

  const onSupplierFilter = (event) => {
    setSupplierFilterData(filterBy(supplierList.slice(), event.filter));
  };

  const onNominalFilter = (event) => {
    setNominalFilterData(filterBy(nominalDisplayList.slice(), event.filter));
  };

  const onFromChange = (e) => {
    setFromDateSelector(e.value ?? null);
    if (e.value) setUseFrom(true);
  };

  const onToChange = (e) => {
    setToDateSelector(e.value ?? null);
    if (e.value) setUseTo(true);
  };

  const handleRefresh = () => {
    fetchData(true);
  };

  const cloneSortDescriptors = (sort) =>
    sort?.map((s) => ({ field: s.field, dir: s.dir }));

  const dataStateChange = (e) => {
    const incoming = e.dataState;
    const sort = cloneSortDescriptors(incoming.sort);

    if (
      sort !== undefined &&
      incoming.skip !== 0 &&
      JSON.stringify(dataState.sort) !== JSON.stringify(sort)
    ) {
      setDataState({ ...incoming, sort, skip: 0 });
    } else if (incoming?.sort?.[0] === undefined) {
      setDataState({
        ...incoming,
        sort: [
          {
            field: dataState?.sort?.[0]?.field ?? "date",
            dir: "asc",
          },
        ],
      });
    } else {
      setDataState({ ...incoming, sort });
    }
  };
  useEffect(() => {
    if (suppressDataStateFetchRef.current) {
      suppressDataStateFetchRef.current = false;
      return;
    }
    fetchData();
  }, [dataState.skip, dataState.take, JSON.stringify(dataState.sort)]);

  useEffect(() => {
    if (skipInitialFilterEffectRef.current) {
      skipInitialFilterEffectRef.current = false;
      return;
    }
    fetchData(true);
  }, [
    matterValue?.matterId,
    supplierValue?.supplierRef,
    nominalValue?.nominalRef,
    useFrom,
    useTo,
    fromDateSelector?.getTime?.(),
    toDateSelector?.getTime?.(),
    searchValue,
  ]);

  const handleCellClick = (cellProps) => {
    setSelectedCell(cellProps);
  };

  const handleOptionClick = (action, cellProps) => {
    const row = cellProps?.dataItem;
    if (!row) return;

    if (action === "viewDetails") {
      const no = row?.transactionNo;
      if (!no) {
        toast.warn("No transaction number on this row.");
        return;
      }
      setDetailsTransNo(no);
      setShowDetails(true);
    }

    if (action === "viewLedger") {
      if (row.matterId || row.matterID) {
        dispatch(
          changeLedgerLink("MatterLedger", {
            matterId: row.matterId || row.matterID,
            matterRef: row.matterRef,
          }),
        );
        navigate("/accounts/matterledger");
      } else {
        toast.info("This transaction is not associated with a matter.");
      }
    }
  };

  return (
    <>
      <div className="page h-100 w-100 d-flex flex-column">
        {showDetails && (
          <ViewPostingDetails
            transNo={detailsTransNo}
            reloadData={() => {}}
            cancelEdit={() => {
              setShowDetails(false);
              setDetailsTransNo("");
            }}
          />
        )}

        <div className="row flex-shrink-0">
          <div className="col-12 d-flex flex-wrap align-items-end gap-2 mb-3">
            <div className="d-flex flex-column">
              <Label className="mb-1 filter-font">Matter</Label>
              <MattersCombobox
                controlled
                value={matterValue}
                onChangematter={onMatterChange}
                onFilterChange={() => setMatterValue(null)}
                width={200}
              />
            </div>

            <div className="d-flex flex-column">
              <Label className="mb-1 filter-font">Supplier</Label>
              <ComboBox
                style={{ width: "200px" }}
                data={supplierFilterData}
                textField="representation"
                dataItemKey="supplierRef"
                filterable
                onFilterChange={onSupplierFilter}
                value={supplierValue}
                onChange={onSupplierChange}
                placeholder="Supplier"
              />
            </div>

            <div className="d-flex flex-column">
              <Label className="mb-1 filter-font">Nominal</Label>
              <ComboBox
                style={{ width: "200px" }}
                data={nominalFilterData}
                textField="display"
                dataItemKey="nominalRef"
                filterable
                onFilterChange={onNominalFilter}
                value={nominalValue}
                onChange={onNominalChange}
                placeholder="Nominal"
              />
            </div>

            <div className="flex-shrink-0 mt-1">
              <div className="nominal-form-to-parent-row">
                <div className="labels-containers">
                  <Label editorId="ts-from" className="filter-font">
                    From
                  </Label>
                  <div className="label-to">
                    <Label editorId="ts-to" className="filter-font">
                      To
                    </Label>
                  </div>
                </div>
              </div>
              <div className="nominal-form-to-row">
                <div
                  className="date-form-to-wrapper"
                  style={{ height: "40px" }}
                >
                  <div className="from">
                    <div className="d-flex align-items-center justify-content-center">
                      <Checkbox
                        value={useFrom}
                        className="ms-2 me-1"
                        onChange={(e) => setUseFrom(e.value)}
                      />
                      <DatePicker
                        className="date-picker"
                        value={fromDateSelector}
                        onChange={onFromChange}
                        toggleButton={CalenderCustomToggleButton}
                      />
                    </div>
                  </div>
                  <div className="divider" />
                  <div className="to">
                    <div className="d-flex align-items-center justify-content-center">
                      <Checkbox
                        value={useTo}
                        className="ms-2 me-1"
                        onChange={(e) => setUseTo(e.value)}
                      />
                      <DatePicker
                        className="date-picker"
                        value={toDateSelector}
                        onChange={onToChange}
                        toggleButton={CalenderCustomToggleButton}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                flex: "1 1 0%",
                minWidth: "200px",
                maxWidth: "280px",
              }}
            >
              <SearchTextBox
                ref={searchRef}
                changeFilterValue={setSearchValue}
                width="100%"
                height="40px"
              />
            </div>

            <Button
              className="common-btn"
              onClick={handleRefresh}
              svgIcon={arrowRotateCwIcon}
              title="Refresh"
            >
              <span className="d-flex align-items-center">
                <span className="d-xl-inline-block d-sm-none ms-1">
                  Refresh
                </span>
              </span>
            </Button>
          </div>
        </div>

        <div className="flex-grow-1 overflow-auto min-h-0">
          <Grid
            data={list.data || []}
            total={list.total || 0}
            skip={dataState.skip}
            take={dataState.take}
            sort={dataState.sort}
            dataItemKey="transactionNo"
            onDataStateChange={dataStateChange}
            className="fs-md h-100 overflow-auto grid-minwidth-col5-150"
            sortable={true}
            pageable={{
              pageSizes: GRID_PAGE_SIZES,
              previousNext: true,
              buttonCount: 5,
            }}
            showLoader={loading}
            loader={<GridLoader />}
          >
            <GridNoRecords>
              {!loading && (!list.data || list.data.length === 0)
                ? "No records available"
                : null}
            </GridNoRecords>

            <GridColumn
              field="date"
              title="Date"
              width="120px"
              cell={DateFormatCell}
            />
            <GridColumn field="postingType" title="Type" width="80px" />
            <GridColumn field="matterRef" title="Matter Ref" width="110px" />
            <GridColumn field="reference" title="Ref" width="120px" />

            <GridColumn
              field="details"
              title="Payee / Description"
              cell={PayeeDescCell}
            />

            <GridColumn
              field="supplierRef"
              title="Supplier Ref"
              width="120px"
            />
            <GridColumn field="nominalRef" title="Nominal Ref" width="120px" />

            <GridColumn
              field="vatAmount"
              title="VAT"
              width="100px"
              cell={NullableAmountCell}
              headerClassName="grid-header-center"
            />

            <GridColumn
              field="grossAmount"
              title="Net"
              width="110px"
              cell={NetAmountCell}
              headerClassName="grid-header-center"
            />

            <GridColumn
              field="grossAmount"
              title="Total"
              width="110px"
              cell={NullableAmountCell}
              headerClassName="grid-header-center"
            />
            <GridColumn
              title=" "
              width="50px"
              cell={(props) => (
                <TransactionSearchRowMenu
                  {...props}
                  handleOptionClick={handleOptionClick}
                  handleCellClick={handleCellClick}
                  selectedCell={selectedCell}
                />
              )}
            />
            <GridColumn field="" title="" width="10px" />
          </Grid>
        </div>
      </div>
    </>
  );
};

export default TransactionSearch;
